import { useState, useRef, useCallback, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker, Libraries } from '@react-google-maps/api';
import { Loader2 } from "lucide-react";
import { envConfig } from "@/shared/services/apiConfig";
import { AddressData } from "@/shared/types/addressTypes";
import { logger } from "@/shared/utils/logger";
import SearchBar from "@/shared/components/SearchBar";
import { Button } from "@/shared/ui/button";

const libraries: Libraries = ['places'];

function parseAddressComponents(addressComponents: google.maps.GeocoderAddressComponent[]) {
  const addressData = {
    locality: '',
    administrativeArea: '',
    administrativeAreaCode: '',
    postalCode: '',
    regionCode: '',
    region: '',
  };

  addressComponents.forEach((component) => {
    const types = component.types;
    if (types.includes('locality')) {
      addressData.locality = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      addressData.administrativeArea = component.long_name;
      addressData.administrativeAreaCode = component.short_name;
    } else if (types.includes('postal_code')) {
      addressData.postalCode = component.long_name;
    } else if (types.includes('country')) {
      addressData.regionCode = component.short_name;
      addressData.region = component.long_name;
    }
  });

  return addressData;
}

function createAddressData(
  formattedAddress: string,
  latitude: number,
  longitude: number,
  addressComponents: google.maps.GeocoderAddressComponent[],
  placeId?: string,
): SearchResult {
  const parsedComponents = parseAddressComponents(addressComponents);

  return {
    addressLines: [formattedAddress],
    formattedAddress,
    latitude,
    longitude,
    placeId: placeId || '',
    ...parsedComponents,
  };
}

export interface SearchResult extends AddressData {
  placeId: string;
}

interface AddressMapSelectorProps {
  onAddressSelect: (address: SearchResult) => void;
  selectedAddress: SearchResult | null;
  mapCenter?: google.maps.LatLngLiteral;
  onMapCenterChange?: (center: google.maps.LatLngLiteral) => void;
  height?: string;
  showSearch?: boolean;
  placeholder?: string;
}

export const AddressMapSelector = ({
  onAddressSelect,
  selectedAddress,
  mapCenter = { lat: 64.1355, lng: -21.8954 }, // Default to Reykjavik
  onMapCenterChange,
  showSearch = true,
  placeholder = "Search for an address..."
}: AddressMapSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentMapCenter, setCurrentMapCenter] = useState<google.maps.LatLngLiteral>(mapCenter);

  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Google Maps API loader
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: envConfig.googleMapsApiKey,
    libraries,
  });

  // Handle address search
  const handleAddressSearch = useCallback(async (query: string) => {
    if (!autocompleteService.current) return;

    setIsSearching(true);
    try {
      const response = await autocompleteService.current.getPlacePredictions({
        input: query,
        types: ['address'],
      });

      if (response.predictions) {
        const results = await Promise.all(
          response.predictions.map(async (prediction) => {
            if (!placesService.current && mapRef.current) {
              placesService.current = new google.maps.places.PlacesService(mapRef.current);
            }

            return new Promise<SearchResult>((resolve) => {
              if (placesService.current && prediction.place_id) {
                placesService.current.getDetails(
                  { placeId: prediction.place_id, fields: ['address_components', 'geometry', 'formatted_address'] },
                  (place, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                      const addressComponents = place.address_components || [];
                      const result = createAddressData(
                        place.formatted_address || '',
                        place.geometry?.location?.lat() || 0,
                        place.geometry?.location?.lng() || 0,
                        addressComponents,
                        prediction.place_id
                      );

                      resolve(result);
                    }
                  }
                );
              }
            });
          })
        );
        setSearchResults(results);
      }
    } catch (error) {
      logger.error('Address search error', { error }, 'AddressMapSelector');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change
  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleAddressSearch(value);
    }, 500);
  };

  // Handle search result selection
  const handleSelectSearchResult = async (result: SearchResult) => {
    if (!placesService.current) return;

    try {
      const place = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
        placesService.current?.getDetails(
          { placeId: result.placeId, fields: ['geometry', 'formatted_address'] },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              resolve(place);
            } else {
              reject(new Error('Failed to get place details'));
            }
          }
        );
      });

      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Use the complete result data that already has parsed address components
        const addressData: SearchResult = {
          ...result, // Include all parsed address components
          latitude: lat,
          longitude: lng,
        };

        onAddressSelect(addressData);
        const newCenter = { lat, lng };
        setCurrentMapCenter(newCenter);
        if (onMapCenterChange) {
          onMapCenterChange(newCenter);
        }
        setSearchQuery(result.formattedAddress);
        setSearchResults([]);
      }
    } catch (error) {
      logger.error('Error getting place details', { error }, 'AddressMapSelector');
    }
  };

  // Handle map location selection
  const handleLocationSelect = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // Reverse geocode to get address
      if (placesService.current) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            // Parse address components from reverse geocoding results
            const addressComponents = results[0].address_components || [];
            const parsedComponents = parseAddressComponents(addressComponents);

            const addressData: SearchResult = {
              formattedAddress: results[0].formatted_address,
              latitude: lat,
              longitude: lng,
              placeId: '', // No place ID for reverse geocoding
              ...parsedComponents, // Include parsed address components
            };
            onAddressSelect(addressData);
            const newCenter = { lat, lng };
            setCurrentMapCenter(newCenter);
            if (onMapCenterChange) {
              onMapCenterChange(newCenter);
            }
            setSearchQuery(results[0].formatted_address);
          }
        });
      }
    }
  };

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    placesService.current = new google.maps.places.PlacesService(map);
  }, []);

  // Initialize autocomplete service when loaded
  const initializeServices = useCallback(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  // Initialize services when component mounts
  useEffect(() => {
    initializeServices();
  }, [initializeServices]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-[300px] border rounded-md">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showSearch && (
        <div className="space-y-2">
          <div className="relative">
            <SearchBar value={searchQuery} onChange={(value) => handleSearchInputChange(value)} placeholder={placeholder} />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-background rounded-md shadow-lg max-h-60 overflow-auto border">
              {searchResults.map((result, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  className="w-full px-4 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none border-b last:border-b-0 rounded-none justify-start"
                  onClick={() => handleSelectSearchResult(result)}
                >
                  {result.formattedAddress}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="h-[300px] w-full rounded-md border">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={currentMapCenter}
          zoom={15}
          onClick={handleLocationSelect}
          onLoad={onMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {selectedAddress && (
            <Marker
              position={{ lat: selectedAddress.latitude, lng: selectedAddress.longitude }}
            />
          )}
        </GoogleMap>
      </div>

      {selectedAddress && (
        <div className="p-3 bg-muted rounded-md">
          <p className="text-sm font-medium">Selected Address:</p>
          <p className="text-sm">{selectedAddress.formattedAddress}</p>
          <p className="text-xs">
            Coordinates: {selectedAddress.latitude.toFixed(6)}, {selectedAddress.longitude.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};
